/**
 * The console's pages and how the left rail groups them (the overhaul's information architecture, README section 2).
 * Pure data and helpers, no React, so test/shell-routes.test.ts proves them.
 *
 * A page's id is the tab id the console has always used (lib/deepLink.ts ADMIN_TABS), and its path is `/<id>`, so every
 * link written before the router (`/support?request=<id>`, `/?tab=caps`) still lands on the same screen. The labels
 * are the new ones. Screens not yet rebuilt render inside the new shell under their new name until their turn in the
 * plan; a group appears in the rail once it holds a page.
 */
import { ADMIN_TABS, tabFromLocation, type Tab } from '../lib/deepLink';

export type PageId = Tab;

export type GroupId = 'operations' | 'pulse' | 'people' | 'knowledge' | 'outfits' | 'settings' | 'audit';

export interface PageDef {
  id: PageId;
  /** The page title and its name in the rail. */
  label: string;
  group: GroupId;
  /** One line for the Jump to box. */
  summary: string;
  /** Words people search for that are not in the label. */
  keywords: string[];
}

export interface NavItem {
  /** The id the rail and the Jump to box know the item by. */
  id: string;
  label: string;
  /** The pages this item opens; the first is where a click goes. More than one shows them as tabs in the page header. */
  pages: PageId[];
}

export interface NavGroup {
  id: GroupId;
  label: string;
  items: NavItem[];
  /** The rail can fold this group away (Settings). */
  collapsible?: boolean;
}

export const PAGES: Record<PageId, PageDef> = {
  dashboard: { id: 'dashboard', label: 'Overview', group: 'operations', summary: 'Cost, requests, spend against the caps, service health', keywords: ['dashboard', 'home', 'cost', 'spend', 'usage', 'health', 'tokens'] },
  support: { id: 'support', label: 'Support', group: 'operations', summary: 'Devices, IT requests and remote support sessions', keywords: ['it support', 'devices', 'requests', 'sessions', 'diagnostics', 'remote'] },
  'pc-switches': {
    id: 'pc-switches', label: 'PC switches', group: 'operations',
    summary: 'The switches every PC with Gyro follows, exceptions for people, and what each PC runs with',
    keywords: ['intune', 'managed config', 'policy', 'desktop', 'exceptions', 'autonomy', 'local connections', 'mcp', 'allow list', 'update ring', 'updates', 'revit', 'claude', 'elevation'],
  },
  caps: { id: 'caps', label: 'Caps & limits', group: 'operations', summary: 'Fleet caps, per-person caps, answer and usage limits', keywords: ['usage caps', 'budget', 'spend cap', 'limits', 'rate', 'quota'] },
  analytics: {
    id: 'analytics', label: 'Analytics', group: 'operations',
    summary: 'What gets used, by whom and how often: realtime, screens, actions, sessions, journeys, retention, people, tech; cost by app',
    keywords: ['usage analytics', 'realtime', 'screens', 'actions', 'sessions', 'journeys', 'retention', 'people', 'tech', 'audience', 'internal team', 'quality', 'tools', 'skill roi', 'chargeback', 'approval cards'],
  },
  adoption: { id: 'adoption', label: 'Adoption', group: 'operations', summary: 'Active people, stickiness and the heaviest users', keywords: ['dau', 'mau', 'active users', 'engagement', 'top users'] },
  'office-installs': { id: 'office-installs', label: 'Office installs', group: 'operations', summary: 'Add-in installs and revoke', keywords: ['office', 'add-in', 'outlook', 'word', 'excel', 'revoke'] },
  pulse: { id: 'pulse', label: 'Overview', group: 'pulse', summary: 'What people asked Gyro, whether it helped, and what to fix; anonymous', keywords: ['pulse', 'helped', 'could not', 'asks', 'digest', 'needs a look', 'failing'] },
  'pulse-asks': { id: 'pulse-asks', label: 'Asks', group: 'pulse', summary: 'Every intent people ask for, with its outcomes, trend and records', keywords: ['pulse', 'intents', 'questions', 'records', 'outcomes', 'repeat'] },
  'pulse-domains': { id: 'pulse-domains', label: 'Domains', group: 'pulse', summary: 'One scorecard per domain: Gyro AI for Revit, Bluebeam Revu, PPP, Microsoft 365 and the rest', keywords: ['pulse', 'scorecard', 'revit', 'revu', 'ppp', 'performed', 'said it could not', 'tried and failed'] },
  'pulse-tools': { id: 'pulse-tools', label: 'Tools & connectors', group: 'pulse', summary: 'Every tool, MCP server and connected app with its health, and the rules behind it', keywords: ['pulse', 'health', 'failing', 'degraded', 'release', 'mute', 'mcp'] },
  'pulse-feedback': { id: 'pulse-feedback', label: 'Feedback', group: 'pulse', summary: 'What people said right after Gyro did something: the cards and thumbs, anonymous', keywords: ['pulse', 'cards', 'worked', 'partly', "didn't work", 'undo', 'comments', 'thumbs', 'reviewed'] },
  'pulse-problems': { id: 'pulse-problems', label: 'Problems', group: 'pulse', summary: 'Recurring problems the analyst opened, with the evidence, the likely cause and the suggested fix', keywords: ['pulse', 'problem files', 'evidence', 'likely cause', 'fixed in', 'the lab', 'verification'] },
  'pulse-digests': { id: 'pulse-digests', label: 'Digests', group: 'pulse', summary: 'The daily and weekly digests the analyst writes, every claim linked to its records', keywords: ['pulse', 'daily digest', 'weekly digest', 'for the dev team', 'run now', 'mark read'] },
  'pulse-operations': { id: 'pulse-operations', label: 'Operations', group: 'pulse', summary: "What Gyro's answers cost, how fast they are, how approvals flow, how the models behave, and where knowledge is missing", keywords: ['pulse', 'cost per outcome', 'speed', 'time to first word', 'approval funnel', 'model health', 'provider errors', 'cache hit', 'knowledge gaps', 'world iq'] },
  'pulse-settings': { id: 'pulse-settings', label: 'Settings', group: 'pulse', summary: 'Feedback cards, what is kept, the lock mode, the health thresholds, the models and budget, the digest times, The Lab, erase all text', keywords: ['pulse', 'lock mode', 'warn', 'second call', 'thresholds', 'retention', 'budget', 'digest time', 'erase', 'cards per day'] },
  fleet: { id: 'fleet', label: 'Fleet health', group: 'operations', summary: 'Every PC with Gyro: its version, when it was last seen, crashes, WebView2 and sign-in failures, switches and updates', keywords: ['fleet', 'pcs', 'devices', 'versions', 'crashes', 'webview2', 'sign-in', 'not seen', 'online', 'push', 'mcp', 'local connections', 'updates', 'ota', 'problem pcs'] },
  trends: { id: 'trends', label: 'Trends', group: 'audit', summary: 'How the rules are holding up: DLP blocks, refusals by risk tier, decisions, approvals, the Bridge and receivers, elevation', keywords: ['governance', 'dlp', 'refusals', 'risk tier', 'auto-run', 'approvals', 'time to approve', 'bridge', 'receivers', 'elevation', 'admin changes', 'custody', 'governance copy'] },
  access: { id: 'access', label: 'Access & roles', group: 'people', summary: 'Tool role overrides and the second-approver queue', keywords: ['roles', 'rbac', 'tools', 'permissions', 'second approver'] },
  knowledge: { id: 'knowledge', label: 'Company knowledge', group: 'knowledge', summary: 'Projects, folders, pools, sources and removals', keywords: ['knowledge', 'projects', 'dropbox', 'folders', 'pools', 'removals', 'party report'] },
  skills: { id: 'skills', label: 'Skills', group: 'knowledge', summary: 'Loaded reference skills', keywords: ['skills', 'reference', 'reload'] },
  pets: { id: 'pets', label: 'AI PETS', group: 'knowledge', summary: 'Companion service readiness and employee offboarding; reviews are in Approvals', keywords: ['pets', 'library', 'community', 'approved', 'moderation', 'agents', 'workers'] },
  'company-skills': { id: 'company-skills', label: 'Company skills', group: 'knowledge', summary: "Skills published for everyone, and promoting a person's skill", keywords: ['company skills', 'publish', 'promote', 'retire', 'saved skills', 'workflows'] },
  config: { id: 'config', label: 'Model & key', group: 'settings', summary: 'Model, autonomy, model catalog and the Anthropic key', keywords: ['configuration', 'settings', 'model', 'autonomy', 'api key', 'anthropic'] },
  apps: { id: 'apps', label: 'Apps & surfaces', group: 'settings', summary: 'Registered apps, autonomy, risk ceiling and web search', keywords: ['apps', 'surfaces', 'web search', 'the lab', 'risk ceiling'] },
  voice: { id: 'voice', label: 'Voice', group: 'settings', summary: 'ElevenLabs key, tuning, usage and the voice library', keywords: ['elevenlabs', 'voice', 'speech'] },
  governance: {
    id: 'governance', label: 'Governance', group: 'settings',
    summary: 'Persona, flags, connectors, webhooks, data protection, personal reports, erasure',
    keywords: ['persona', 'house rules', 'feature flags', 'connectors', 'webhooks', 'dlp', 'sensitivity labels', 'office add-in', 'ppp', 'personal reports', 'erasure', 'data requests'],
  },
  outfits: {
    id: 'outfits', label: 'Gallery', group: 'outfits',
    summary: "The desktop mascot's outfits: the gallery, day outfits, categories and who always sees every outfit",
    keywords: ['outfits', 'mascot', 'gallery', 'day outfits', 'categories', 'featured', 'retired', 'costume', 'desktop'],
  },
  activity: { id: 'activity', label: 'Activity', group: 'audit', summary: 'Every Gyro turn: the trace explorer', keywords: ['traces', 'turns', 'errors', 'tools'] },
  audit: { id: 'audit', label: 'Audit log', group: 'audit', summary: 'The tamper-evident audit ledger and its export', keywords: ['audit', 'ledger', 'chain', 'export', 'admin changes'] },
  approvals: { id: 'approvals', label: 'Approvals', group: 'audit', summary: 'The one inbox for pending actions', keywords: ['approvals', 'pending actions', 'inbox', 'approve', 'reject', 'second approver'] },
};

export const GROUP_LABELS: Record<GroupId, string> = {
  operations: 'Operations',
  pulse: 'Pulse',
  people: 'People',
  knowledge: 'Knowledge',
  outfits: 'Outfits and mascot',
  settings: 'Settings',
  audit: 'Audit',
};

/**
 * The rail, top to bottom. `officeInstalls`, `pcSwitches` and `pulse` are whether those pages are offered (App.tsx probes
 * each: the service answers 404 where it does not run them).
 */
export function navGroups(opts: { officeInstalls: boolean; pets?: boolean; pcSwitches?: boolean; pulse?: boolean; fleet?: boolean; trends?: boolean }): NavGroup[] {
  const item = (id: PageId, label = PAGES[id].label): NavItem => ({ id, label, pages: [id] });
  const groups: NavGroup[] = [
    {
      id: 'operations', label: GROUP_LABELS.operations,
      items: [
        item('dashboard'),
        item('support'),
        ...(opts.pcSwitches === true ? [item('pc-switches')] : []),
        ...(opts.fleet === true ? [item('fleet')] : []),
        item('caps'),
        { id: 'usage', label: 'Usage & adoption', pages: ['analytics', 'adoption'] },
        ...(opts.officeInstalls ? [item('office-installs')] : []),
      ],
    },
    // Pulse: the owner's order (strategy section 9, mockups admin-7 to admin-13), Operations after Digests and before Settings.
    {
      id: 'pulse', label: GROUP_LABELS.pulse,
      items: opts.pulse === true
        ? [item('pulse'), item('pulse-asks'), item('pulse-domains'), item('pulse-tools'), item('pulse-feedback'), item('pulse-problems'), item('pulse-digests'), item('pulse-operations'), item('pulse-settings')]
        : [],
    },
    { id: 'people', label: GROUP_LABELS.people, items: [item('access')] },
    { id: 'knowledge', label: GROUP_LABELS.knowledge, items: [item('knowledge'), item('skills'), item('company-skills'), ...(opts.pets === true ? [item('pets')] : [])] },
    { id: 'outfits', label: GROUP_LABELS.outfits, items: [item('outfits')] },
    { id: 'settings', label: GROUP_LABELS.settings, collapsible: true, items: [item('config'), item('apps'), item('voice'), item('governance')] },
    { id: 'audit', label: GROUP_LABELS.audit, items: [item('activity'), item('audit'), ...(opts.trends === true ? [item('trends')] : []), item('approvals')] },
  ];
  return groups.filter((group) => group.items.length > 0);
}

/** The rail item that holds `page`. */
export function navItemFor(page: PageId, groups: NavGroup[]): NavItem | undefined {
  for (const group of groups) for (const it of group.items) if (it.pages.includes(page)) return it;
  return undefined;
}

/** The rail group that holds `page`. */
export function navGroupFor(page: PageId, groups: NavGroup[]): NavGroup | undefined {
  return groups.find((group) => group.items.some((it) => it.pages.includes(page)));
}

/** `/caps`: a page's own path. */
export function pathFor(page: PageId): string {
  return `/${page}`;
}

/**
 * The page a location shows. The path wins over `?tab=` (lib/deepLink.ts tabFromLocation); anything unknown is the
 * fallback (Overview for an admin, Activity for an auditor). On load App.tsx also rewrites a path that is no page to
 * `/` (canonicalLocation), so the address bar never keeps one while Overview is marked active.
 */
export function pageFor(pathname: string, search: string, fallback: PageId): PageId {
  return tabFromLocation(pathname, search, fallback);
}

const isTab = (value: string): value is Tab => (ADMIN_TABS as readonly string[]).includes(value);

/**
 * The location a link written before the router should be rewritten to, or null when it is already canonical:
 * `/?tab=support&request=abc` becomes `/support?request=abc` (every other query parameter is kept), and a path that is
 * no page (`/no-such-page`) becomes `/`.
 */
export function canonicalLocation(pathname: string, search: string): string | null {
  const params = new URLSearchParams(search);
  const tab = (params.get('tab') ?? '').trim().toLowerCase();
  const seg = pathname.replace(/^\/+|\/+$/g, '').toLowerCase();
  if (isTab(seg)) return null;
  if (isTab(tab)) {
    params.delete('tab');
    const rest = params.toString();
    return `/${tab}${rest ? `?${rest}` : ''}`;
  }
  // The root shows the landing page as it is; a path that is no page goes to the root (App.tsx replaces the history
  // entry), so the address bar never shows a page that does not exist.
  return seg ? '/' : null;
}

/** A place inside a page the Jump to box can take you to: the panel whose title starts with `find`. */
export interface SectionDef {
  label: string;
  page: PageId;
  find: string;
  /** The page's `?view=` that holds the section, for a page that opens on another view (Analytics opens on Overview). */
  view?: string;
  keywords?: string[];
}

export const SECTIONS: SectionDef[] = [
  { label: 'Spend against fleet caps', page: 'dashboard', find: 'Spend against fleet caps', keywords: ['budget'] },
  { label: 'Service health', page: 'dashboard', find: 'Service health', keywords: ['ledger', 'history storage'] },
  { label: 'Usage by app', page: 'dashboard', find: 'Usage by app' },
  { label: 'What Gyro could not do', page: 'dashboard', find: 'What Gyro could not do', keywords: ['unmet', 'roadmap'] },
  { label: 'Global caps', page: 'caps', find: 'Global caps', keywords: ['fleet caps', 'budget'] },
  { label: 'Default per-person cap', page: 'caps', find: 'Default per-user cap' },
  { label: 'Per-person override', page: 'caps', find: 'Per-user override' },
  { label: 'Current usage', page: 'caps', find: 'Current usage' },
  { label: 'Answer and usage limits', page: 'caps', find: 'Answer and usage limits', keywords: ['thinking depth', 'tool rounds', 'longest answer'] },
  { label: 'Outfit gallery', page: 'outfits', find: 'Outfits', keywords: ['mascot', 'gallery', 'featured', 'tiles'] },
  { label: 'Add an outfit', page: 'outfits', find: 'Add an outfit', keywords: ['upload', 'png'] },
  { label: 'Add several outfits', page: 'outfits', find: 'Add several outfits', keywords: ['bulk', 'folder'] },
  { label: 'Outfit categories', page: 'outfits', find: 'Categories', keywords: ['season'] },
  { label: 'Always see every outfit', page: 'outfits', find: 'Always see every outfit', keywords: ['principals', 'group', 'access'] },
  { label: 'Day outfits', page: 'outfits', find: 'Day outfits', keywords: ['holiday', 'greeting', 'retired'] },
  { label: 'Persona & house rules', page: 'governance', find: 'Persona & house rules' },
  { label: 'Feature flags', page: 'governance', find: 'Feature flags', keywords: ['rollout'] },
  { label: 'Connectors', page: 'governance', find: 'Connectors', keywords: ['oauth'] },
  { label: 'Outbound webhooks', page: 'governance', find: 'Outbound webhooks' },
  { label: 'DLP redaction policy', page: 'governance', find: 'DLP redaction policy', keywords: ['data protection', 'deny terms'] },
  { label: 'Sensitivity labels', page: 'governance', find: 'Sensitivity labels', keywords: ['data protection', 'purview'] },
  { label: 'Office add-in', page: 'governance', find: 'Office add-in' },
  { label: 'Create my PPP', page: 'governance', find: 'Create my PPP', keywords: ['personal reports', 'ppp'] },
  { label: 'Right to erasure', page: 'governance', find: 'Right to erasure', keywords: ['data requests', 'erase', 'delete conversations'] },
  { label: 'Model & autonomy', page: 'config', find: 'Model & Autonomy' },
  { label: 'Model catalog', page: 'config', find: 'Model catalog' },
  { label: 'Anthropic API key', page: 'config', find: 'Anthropic API Key' },
  { label: 'Registered apps', page: 'apps', find: 'Registered apps' },
  { label: 'The Lab', page: 'apps', find: 'The Lab', keywords: ['bug reports'] },
  { label: 'Voice tuning', page: 'voice', find: 'Tuning' },
  { label: 'ElevenLabs usage', page: 'voice', find: 'ElevenLabs usage' },
  { label: 'Top users by spend', page: 'analytics', view: 'cost', find: 'Top users by spend', keywords: ['chargeback', 'cost'] },
  { label: 'Skill ROI', page: 'analytics', view: 'cost', find: 'Skill ROI', keywords: ['cost'] },
  { label: 'Internal people', page: 'analytics', view: 'people', find: 'Internal people', keywords: ['audience', 'dev team', 'qa', 'marked internal', 'import group'] },
  { label: 'Retention cohorts', page: 'analytics', view: 'retention', find: 'Retention', keywords: ['cohort', 'came back', 'weekly'] },
  { label: 'Pending actions', page: 'approvals', find: 'Pending actions', keywords: ['approve', 'reject'] },
  // Pulse
  { label: 'Helped rate by domain', page: 'pulse', find: 'Helped rate by domain', keywords: ['pulse'] },
  { label: 'Asked for but not possible', page: 'pulse', find: 'Asked for but not possible', keywords: ['pulse', 'no tool', 'not connected'] },
  { label: "Today's digest", page: 'pulse', find: "Today's digest", keywords: ['pulse'] },
  { label: 'Needs a look', page: 'pulse', find: 'Needs a look', keywords: ['pulse', 'open problems', 'not yet reviewed', 'spend'] },
  { label: 'Health rules', page: 'pulse-tools', find: 'Health rules', keywords: ['pulse', 'degraded', 'failing', 'recovering', 'release'] },
  { label: 'Cost per outcome', page: 'pulse-operations', find: 'Cost per outcome', keywords: ['pulse', 'unhelpful intents'] },
  { label: 'Speed', page: 'pulse-operations', find: 'Speed', keywords: ['pulse', 'p95', 'slowest tools', 'first word'] },
  { label: 'Approval funnel', page: 'pulse-operations', find: 'Approval funnel', keywords: ['pulse', 'proposed', 'confirmed', 'expired'] },
  { label: 'Model and provider health', page: 'pulse-operations', find: 'Model and provider health', keywords: ['pulse', 'fallbacks', 'provider errors', 'cache hit', 'cut off'] },
  { label: 'Knowledge gaps', page: 'pulse-operations', find: 'Knowledge gaps', keywords: ['pulse', 'no source', 'world iq'] },
  { label: 'Lock mode', page: 'pulse-settings', find: 'Lock mode', keywords: ['pulse', 'warn', 'refuse', 'second call'] },
  { label: 'Health thresholds', page: 'pulse-settings', find: 'Health thresholds', keywords: ['pulse', 'failure rate', 'consecutive'] },
  { label: 'Erase all Pulse text', page: 'pulse-settings', find: 'Erase all Pulse text', keywords: ['pulse', 'erase', 'privacy', 'questions', 'answers', 'comments'] },
];

export interface JumpTarget {
  key: string;
  kind: 'page' | 'section';
  label: string;
  /** Where it is: the group for a page, `Page › section` for a section. */
  where: string;
  page: PageId;
  find?: string;
  /** The `?view=` the section lives on, when the page opens elsewhere. */
  view?: string;
}

/** Where a target navigates: the page's path, with `?view=` when the section lives on another view. */
export function targetHref(target: Pick<JumpTarget, 'page' | 'view'>): string {
  return target.view ? `${pathFor(target.page)}?view=${encodeURIComponent(target.view)}` : pathFor(target.page);
}

/** The rail label of the item that holds `page` (Usage & adoption for both of its tabs). */
export function pageTitle(page: PageId, groups: NavGroup[]): string {
  return navItemFor(page, groups)?.label ?? PAGES[page].label;
}

/** Everything the Jump to box can open, for the pages this person is offered. */
export function jumpTargets(groups: NavGroup[]): JumpTarget[] {
  const offered = new Set(groups.flatMap((group) => group.items.flatMap((it) => it.pages)));
  const pages: JumpTarget[] = groups.flatMap((group) =>
    group.items.flatMap((it) => it.pages.map((page) => ({
      key: `page:${page}`,
      kind: 'page' as const,
      label: it.pages.length > 1 ? `${it.label} › ${PAGES[page].label}` : it.label,
      where: group.label,
      page,
    }))),
  );
  const sections: JumpTarget[] = SECTIONS.filter((s) => offered.has(s.page)).map((s) => ({
    key: `section:${s.page}:${s.find}`,
    kind: 'section' as const,
    label: s.label,
    where: `${pageTitle(s.page, groups)} › section`,
    page: s.page,
    find: s.find,
    ...(s.view ? { view: s.view } : {}),
  }));
  return [...pages, ...sections];
}

function haystack(target: JumpTarget): string[] {
  const page = PAGES[target.page];
  const section = target.kind === 'section' ? SECTIONS.find((s) => s.page === target.page && s.find === target.find) : undefined;
  const extra = target.kind === 'page' ? [page.summary, ...page.keywords] : section?.keywords ?? [];
  return [target.label, target.where, ...extra].map((s) => s.toLowerCase());
}

/**
 * The Jump to box's matches for `query`, best first: a label that starts with the query, then a word in it that does,
 * then a label that contains it, then a match in the summary or keywords. On a label match a page comes before its
 * sections; on a keyword match the section comes first (it is the more exact place: "outfit" opens the mascot gallery
 * itself, not the top of Governance). An empty query lists the pages.
 */
export function searchTargets(targets: JumpTarget[], query: string, limit = 9): JumpTarget[] {
  const q = query.trim().toLowerCase();
  if (!q) return targets.filter((t) => t.kind === 'page').slice(0, limit);
  const scored: Array<{ target: JumpTarget; score: number; index: number }> = [];
  targets.forEach((target, index) => {
    const label = target.label.toLowerCase();
    const words = label.split(/[^a-z0-9]+/);
    let score = 0;
    if (label.startsWith(q)) score = 4;
    else if (words.some((w) => w.startsWith(q))) score = 3;
    else if (label.includes(q)) score = 2;
    else if (haystack(target).some((h) => h.includes(q))) score = 1;
    const preferred = score >= 2 ? target.kind === 'page' : target.kind === 'section';
    if (score > 0) scored.push({ target, score: score * 2 + (preferred ? 1 : 0), index });
  });
  scored.sort((a, b) => b.score - a.score || a.index - b.index);
  return scored.slice(0, limit).map((s) => s.target);
}
